import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ScreenerFormItem from '../components/ScreenerFormItem';
import { Alert } from '../../components/shared/Alert';
import axiosInstance from '../../utils/axiosConfig';
import { UUID_REGEX } from '../../utils/constants';
import type {
  ScreenerQuestionnaire,
  AdhocScreenerApiResponse,
} from '../types/screener';
import { calculateQuestionScore } from '../utils/screenerScoring';
import { isTextInputQuestion } from '../types/screener';

const SUBMITTED_STATUS_SET = new Set<string>(['COMPLETED', 'SYNCED', 'SYNC_FAILED']);

const EMPTY_ANSWERS: Record<string, string> = {};

/* ------------------------------------------------------------------ */
/*  Reusable content component (accepts workflowId as prop)            */
/* ------------------------------------------------------------------ */

interface PatientScreenersContentProps {
  workflowId: string;
  onComplete?: () => void;
}

export const PatientScreenersContent: React.FC<PatientScreenersContentProps> = ({ workflowId, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workflowAlreadyCompleted, setWorkflowAlreadyCompleted] = useState(false);
  const [screenerData, setScreenerData] = useState<ScreenerQuestionnaire[]>([]);

  // Per-screener state keyed by screenerTemplateId (UUID string)
  const [answersByScreener, setAnswersByScreener] = useState<Record<string, Record<string, string>>>({});
  const [openScreener, setOpenScreener] = useState<string | null>(null);
  const [submittedScreeners, setSubmittedScreeners] = useState<Set<string>>(new Set());
  const [submittingScreeners, setSubmittingScreeners] = useState<Set<string>>(new Set());

  /* ---------------------- Fetch screener data ---------------------- */

  React.useEffect(() => {
    if (!workflowId) {
      setError('Missing workflow id in URL.');
      return;
    }
    if (!UUID_REGEX.test(workflowId)) {
      setError('Invalid workflow id.');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setWorkflowAlreadyCompleted(false);

    axiosInstance
      .get<AdhocScreenerApiResponse>(
        `/api/adhoc-screener/workflows/${encodeURIComponent(workflowId)}/screeners`,
      )
      .then((res) => {
        if (cancelled) return;

        const envelope = res.data;
        if (!envelope.success || !envelope.data) {
          setError(envelope.message || 'Unable to load screener questions.');
          return;
        }

        const { screeners, workflowStatus } = envelope.data;

        // Filter out cancelled screeners
        const activeScreeners = screeners.filter((s) => s.stepStatus !== 'CANCELLED');

        // Pre-populate already-submitted set from stepStatus
        const alreadySubmitted = new Set<string>();
        activeScreeners.forEach((s) => {
          if (SUBMITTED_STATUS_SET.has(s.stepStatus)) {
            alreadySubmitted.add(s.screenerTemplateId);
          }
        });

        // All done if every active screener is submitted or workflow is COMPLETED
        const allDone =
          workflowStatus === 'COMPLETED' ||
          (activeScreeners.length > 0 && activeScreeners.every((s) => SUBMITTED_STATUS_SET.has(s.stepStatus)));

        if (allDone) {
          setWorkflowAlreadyCompleted(true);
          setScreenerData([]);
          return;
        }

        // Map API items to ScreenerQuestionnaire shape for shared components
        const questionnaires: ScreenerQuestionnaire[] = activeScreeners.map((s) => ({
          questionnaireid: 0,
          templateid: s.screenerTemplateId,
          name: s.name,
          sections: s.sections,
          screenerTemplateId: s.screenerTemplateId,
          stepStatus: s.stepStatus,
        }));

        setScreenerData(questionnaires);
        setSubmittedScreeners(alreadySubmitted);

        // Initialize answer maps for non-submitted screeners
        const initial: Record<string, Record<string, string>> = {};
        questionnaires.forEach((q) => {
          if (!alreadySubmitted.has(q.screenerTemplateId!)) {
            initial[q.screenerTemplateId!] = {};
          }
        });
        setAnswersByScreener(initial);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        const envelope = err.response?.data;
        if (envelope?.errorCode === 'CONFLICT') {
          setWorkflowAlreadyCompleted(true);
        } else {
          setError(envelope?.message || 'Unable to load screener questions. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [workflowId]);

  /* ---------------------- Answer change handler -------------------- */

  const handleAnswerChange = useCallback(
    (screenerTemplateId: string, answerKey: string, value: string) => {
      setAnswersByScreener((prev) => ({
        ...prev,
        [screenerTemplateId]: {
          ...(prev[screenerTemplateId] || {}),
          [answerKey]: value,
        },
      }));
    },
    [],
  );

  /* ---------------------- Submit single screener ------------------- */

  const handleSubmitScreener = useCallback(
    async (templateId: number | string) => {
      const screenerTemplateId = String(templateId);

      if (!workflowId) {
        setError('Missing workflow id in URL.');
        return;
      }

      const questionnaire = screenerData.find((q) => q.screenerTemplateId === screenerTemplateId);
      if (!questionnaire) return;

      const answers = answersByScreener[screenerTemplateId] || {};

      // Validate required questions are answered
      let hasUnansweredRequired = false;
      questionnaire.sections.forEach((section, sectionIndex) => {
        section.questionlist.forEach((q, qIndex) => {
          if (q.required === false) return;
          const hasChoices =
            (q.possibleanswers && q.possibleanswers.length > 0) ||
            (q.options && q.options.length > 0);
          if (!hasChoices) return; // FREE_TEXT not supported yet
          const key = `${screenerTemplateId}_${sectionIndex}_${qIndex}`;
          if (!answers[key]) hasUnansweredRequired = true;
        });
      });

      if (hasUnansweredRequired) {
        setError('Please answer all required questions before submitting this screener.');
        return;
      }

      // Build payload
      const answerPayload: { questionid: number | string; answer: string }[] = [];
      let score = 0;

      questionnaire.sections.forEach((section, sectionIndex) => {
        section.questionlist.forEach((q, qIndex) => {
          const hasPossibleAnswers = q.possibleanswers && q.possibleanswers.length > 0;
          const hasOptions = q.options && q.options.length > 0;
          if (!hasPossibleAnswers && !hasOptions && !isTextInputQuestion(q)) return;
          const key = `${screenerTemplateId}_${sectionIndex}_${qIndex}`;
          const answer = answers[key];
          if (answer) {
            answerPayload.push({ questionid: q.questionid, answer });
            score += calculateQuestionScore(q, answer);
          }
        });
      });

      setSubmittingScreeners((prev) => {
        const s = new Set(prev);
        s.add(screenerTemplateId);
        return s;
      });
      setError(null);

      try {
        const res = await axiosInstance.post(
          `/api/adhoc-screener/workflows/${encodeURIComponent(workflowId)}/submit`,
          {
            screenerTemplateId,
            answers: answerPayload,
            score: String(score),
          },
        );

        const envelope = res.data;
        if (envelope && envelope.success === false) {
          setError(envelope.message || 'Submission failed.');
          return;
        }

        setSubmittedScreeners((prev) => {
          const s = new Set(prev);
          s.add(screenerTemplateId);
          return s;
        });

        if (openScreener === screenerTemplateId) setOpenScreener(null);
      } catch (err: any) {
        const envelope = err.response?.data;
        if (envelope?.errorCode === 'CONFLICT') {
          // Already submitted -- mark as submitted locally
          setSubmittedScreeners((prev) => {
            const s = new Set(prev);
            s.add(screenerTemplateId);
            return s;
          });
          if (openScreener === screenerTemplateId) setOpenScreener(null);
        } else {
          setError(envelope?.message || 'Unable to submit screener. Please try again.');
        }
      } finally {
        setSubmittingScreeners((prev) => {
          const s = new Set(prev);
          s.delete(screenerTemplateId);
          return s;
        });
      }
    },
    [workflowId, screenerData, answersByScreener, openScreener],
  );

  /* ---------------------- Derived state ---------------------------- */

  const allSubmitted =
    screenerData.length > 0 &&
    screenerData.every((q) => submittedScreeners.has(q.screenerTemplateId!));

  // Notify parent when all screeners are submitted
  useEffect(() => {
    if (allSubmitted && onComplete) {
      onComplete();
    }
  }, [allSubmitted, onComplete]);

  /* ---------------------- Render ----------------------------------- */

  return (
    <>
        <h1 className="text-4xl font-semibold text-[#01545D] text-center mb-3">
          Wellness Check
        </h1>
        <div className="text-xl text-[#1C1C1C] text-center mb-6">
          Help us understand how you've been feeling
        </div>

        {loading && (
          <div className="text-sm text-[#5D6A85] mb-2">Loading screener questions&hellip;</div>
        )}

        {error && (
          <div className="w-full max-w-[1200px] mb-2">
            <Alert type="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </div>
        )}

        {workflowAlreadyCompleted && (
          <div className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-10 w-full max-w-[1200px] mt-4">
            <div className="bg-[#E0F2F3] border border-[#01545D] rounded-lg px-6 py-5 flex items-center gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#01545D] flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <div className="text-lg font-semibold text-[#01545D] mb-1">
                  All Screeners Already Completed
                </div>
                <div className="text-sm text-[#1C1C1C]">
                  All screeners have already been completed and submitted.
                </div>
              </div>
            </div>
          </div>
        )}

        {!workflowAlreadyCompleted && (
          <div className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-10 w-full max-w-[1200px] flex flex-col gap-8 mt-4">
            {/* Screener accordion list */}
            <section className="flex flex-col gap-4">
              <div className="text-lg font-semibold text-[#01545D]">Screener Forms</div>

              {screenerData.length === 0 && !loading && (
                <div className="bg-[#E0F2F3] border border-[#01545D] rounded-lg px-6 py-5 flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#01545D] flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="text-lg font-semibold text-[#01545D] mb-1">
                      No Screeners
                    </div>
                    <div className="text-sm text-[#1C1C1C]">
                      There are no screener forms to complete at this time.
                    </div>
                  </div>
                </div>
              )}

              {screenerData.map((questionnaire) => {
                const sid = questionnaire.screenerTemplateId!;
                return (
                  <ScreenerFormItem
                    key={sid}
                    questionnaire={questionnaire}
                    isOpen={openScreener === sid}
                    isSubmitted={submittedScreeners.has(sid)}
                    isSubmitting={submittingScreeners.has(sid)}
                    selectedAnswers={answersByScreener[sid] || EMPTY_ANSWERS}
                    onToggleOpen={() => setOpenScreener(openScreener === sid ? null : sid)}
                    onAnswerChange={(key, value) => handleAnswerChange(sid, key, value)}
                    onSubmit={handleSubmitScreener}
                  />
                );
              })}
            </section>

            {/* Privacy info banner */}
            <div className="flex items-start gap-3 bg-[#F7F8FD] border border-[rgba(1,84,93,0.3)] rounded-lg px-4 py-3 mt-2">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#01545D] text-white text-xs font-bold mt-1">
                i
              </div>
              <div className="flex flex-col gap-1 text-sm text-[#1C1C1C]">
                <div className="font-semibold text-[#01545D]">Your Privacy Matters</div>
                <div>
                  These questions help your provider assess your mental well-being. Your responses
                  are confidential and protected by HIPAA.
                </div>
              </div>
            </div>

            {/* Not-all-submitted warning */}
            {!allSubmitted && screenerData.length > 0 && (
              <div className="flex items-start gap-3 bg-[#FFF3CD] border border-[#FFC107] rounded-lg px-4 py-3 mt-2">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#FFC107] text-white text-xs font-bold">
                  !
                </div>
                <div className="text-sm text-[#856404]">
                  Please complete and submit all screener forms
                </div>
              </div>
            )}

            {/* All-submitted success banner */}
            {allSubmitted && (
              <div className="bg-[#E0F2F3] border border-[#01545D] rounded-lg px-6 py-5 flex items-center gap-4 mt-2">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#01545D] flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-lg font-semibold text-[#01545D] mb-1">
                    All Screeners Submitted
                  </div>
                  <div className="text-sm text-[#1C1C1C]">
                    Thank you! All screener forms have been completed and submitted successfully.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
    </>
  );
};

/* ------------------------------------------------------------------ */
/*  Standalone page wrapper (backward-compatible route)                */
/* ------------------------------------------------------------------ */

const PatientScreeners: React.FC = () => {
  const { workflowId } = useParams<{ workflowId: string }>();

  if (!workflowId) {
    return (
      <div className="min-h-screen bg-[#F7F8FD] flex flex-col items-center justify-center">
        <div className="text-red-600">Missing workflow id in URL.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FD] flex flex-col">
      <div className="flex flex-row px-10 py-6 shadow justify-between bg-white items-center">
        <span className="text-[#01545D] font-semibold text-2xl">Patient Screeners</span>
      </div>
      <main className="flex-grow container mx-auto flex flex-col items-center px-4 py-10">
        <PatientScreenersContent workflowId={workflowId} />
      </main>
    </div>
  );
};

export default PatientScreeners;
